/*!
 *   Copyright 2025 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

@import "../../../scss/vars";

:host {
  display: block;
  width: 100%;
  height: 100%;

  .wrapper {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: stretch;
  }

  input.discovery-input {
    margin: .25rem 0;
    display: inline-block;
    position: relative;
    font-size: var(--warp-view-input-font-size, 1rem);
    border-radius: 0;
    border: none;
    border-top: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
    border-bottom: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
    color: var(--warp-view-input-label-color, $w10-charcoal);
    background-color: var(--warp-view-input-bg-color, $w10-white);
    vertical-align: middle;
    width: calc(100% - 0.750rem);
    height: 34px;
    padding: 1px 5px;

    &[disabled] {
      background-color: var(--warp-view-input-disabled-bg-color, $w10-lightgrey);
      color: var(--warp-view-input-disabled-label-color, $w10-charcoal);
      border: 1px solid var(--warp-view-input-disabled-border-color, $w10-lightgrey);
    }

    &:focus {
      outline: 0;
      box-shadow: 0 0 0 .25rem var(--warp-view-input-focus-outline-color, rgba(13, 110, 253, .25));
    }
  }

  button.discovery-btn {
    margin: .25rem .125rem;
    display: inline-block;
    font-weight: 400;
    line-height: 1.5em;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    font-size: var(--warp-view-button-font-size, 1rem);
    border: 1px solid var(--warp-view-button-border-color, $w10-blue);
    padding: var(--warp-view-button-padding, .375rem .75rem);
    color: var(--warp-view-button-label-color, $w10-white);
    background-color: var(--warp-view-button-bg-color, $w10-blue);
    width: var(--warp-view-button-width, auto);
    border-radius: var(--warp-view-button-border-radius, 0.25rem);
    max-width: 100%;

    &.secondary {
      border: 1px solid var(--warp-view-button-secondary-border-color, $w10-grey);
      background-color: var(--warp-view-button-secondary-bg-color, $w10-grey);
      color: var(--warp-view-button-secondary-label-color, $w10-white);
    }
    &.prev {
      margin: .25rem 0;
      border: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
      color: var(--warp-view-input-label-color, $w10-charcoal);
      background-color: var(--warp-view-input-bg-color, $w10-white);
      border-radius: var(--warp-view-button-border-radius, 0.25rem);
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      width: auto !important;

      &:hover {
        background-color: var(--warp-view-button-secondary-bg-color, $w10-lightgrey);
      }
    }
    &.next {
      margin: .25rem 0;
      border: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
      color: var(--warp-view-input-label-color, $w10-charcoal);
      background-color: var(--warp-view-input-bg-color, $w10-white);
      border-radius: var(--warp-view-button-border-radius, 0.25rem);
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      width: auto !important;

      &:hover {
        background-color: var(--warp-view-button-secondary-bg-color, $w10-lightgrey);
      }
    }
  }

  button:disabled {
    background-color: var(--warp-view-button-disabled-bg-color, $w10-lightgrey) !important;
    color: var(--warp-view-button-disabled-label-color, $w10-charcoal) !important;
    border: 1px solid var(--warp-view-button-disabled-border-color, $w10-lightgrey) !important;
  }

  /**
  * @author: Alumuko https://github.com/alumuko/alumuko
  * @copyright: Copyright (c) 2021 Alumuko. All rights reserved.
  * @license: Licensed under the MIT license. See http://www.opensource.org/licenses/mit-license.php
  * @website: https://github.com/alumuko/vanilla-datetimerange-picker
  *
  * Special thanks to Dan Grossman.
  * This program is base on Dan Grossman's bootstrap-daterangepicker (version 3.1)
  * I just changed the code a bit to not need jquery.
  */

  .daterangepicker {
    position: absolute;
    color: var(--warp-view-tooltip-label-color, $w10-charcoal);
    background-color: var(--warp-view-tooltip-bg-color, $w10-white);
    border-radius: 4px;
    border: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
    width: 528px;
    max-width: none;
    padding: 0;
    margin-top: 7px;
    top: 100px;
    left: 20px;
    z-index: 3001;
    display: none;
    font-size: 15px;
    line-height: 1em;
    .ranges {
      display: none;
    }

    &.with-range {
      width: 811px;

      .ranges {
        display: block;
      }
    }

    &.sm {
      width: 278px;
    }

    .dp-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  }

  .daterangepicker:before, .daterangepicker:after {
    position: absolute;
    display: inline-block;
    border-bottom-color: var(--warp-view-tooltip-border-color, $w10-lightgrey);
    content: '';
  }

  .daterangepicker:before {
    top: -7px;
    border-right: 7px solid transparent;
    border-left: 7px solid transparent;
    border-bottom: 7px solid  var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker:after {
    top: -6px;
    border-right: 6px solid transparent;
    border-bottom: 6px solid var(--warp-view-tooltip-bg-color, $w10-white);
    border-left: 6px solid transparent;
  }

  .daterangepicker.opensleft:before {
    right: var(--pos, 9px);
  }

  .daterangepicker.opensleft:after {
    right: calc(var(--pos, 9px) + 1);
  }

  .daterangepicker.openscenter:before {
    left: 0;
    right: 0;
    width: 0;
    margin-left: auto;
    margin-right: auto;
  }

  .daterangepicker.openscenter:after {
    left: 0;
    right: 0;
    width: 0;
    margin-left: auto;
    margin-right: auto;
  }

  .daterangepicker.opensright:before {
    left: var(--pos, 9px);
  }

  .daterangepicker.opensright:after {
    left: calc(var(--pos, 9px) + 1px);
  }

  .daterangepicker.drop-up {
    margin-top: -7px;
  }

  .daterangepicker.drop-up:before {
    top: initial;
    bottom: -7px;
    border-bottom: initial;
    border-top: 7px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker.drop-up:after {
    top: initial;
    bottom: -6px;
    border-bottom: initial;
    border-top: 6px solid var(--warp-view-tooltip-bg-color, $w10-white);
  }

  .daterangepicker.single .daterangepicker .ranges, .daterangepicker.single .drp-calendar {
    float: none;
  }

  .daterangepicker.single .drp-selected {
    display: none;
  }

  .daterangepicker.show-calendar .drp-calendar {
    display: block;
  }

  .daterangepicker.show-calendar .drp-buttons {
    display: block;
  }

  .daterangepicker.auto-apply .drp-buttons {
    display: none;
  }

  .daterangepicker .drp-calendar {
    display: none;
    max-width: 270px;
  }

  .daterangepicker .drp-calendar.left {
    padding: 8px 0 8px 8px;
  }

  .daterangepicker .drp-calendar.right {
    padding: 8px;
  }

  .daterangepicker .drp-calendar.single .calendar-table {
    border: none;
  }

  .daterangepicker .calendar-table .next span, .daterangepicker .calendar-table .prev span {
    color: var(--warp-view-tooltip-bg-color, $w10-white);
    border: solid var(--warp-view-input-label-color, $w10-charcoal);
    border-width: 0 2px 2px 0;
    border-radius: 0;
    display: inline-block;
    padding: 3px;
  }

  .daterangepicker .calendar-table .next span {
    transform: rotate(-45deg);
    -webkit-transform: rotate(-45deg);
  }

  .daterangepicker .calendar-table .prev span {
    transform: rotate(135deg);
    -webkit-transform: rotate(135deg);
  }

  .daterangepicker .calendar-table th, .daterangepicker .calendar-table td {
    white-space: nowrap;
    text-align: center;
    vertical-align: middle;
    min-width: 32px;
    width: 32px;
    height: 24px;
    line-height: 24px;
    font-size: 12px;
    border-radius: 4px;
    border: 1px solid transparent;
    cursor: pointer;
  }

  .daterangepicker .calendar-table {
    border: 1px solid var(--warp-view-tooltip-bg-color, $w10-white);
    border-radius: 4px;
    background-color: var(--warp-view-tooltip-bg-color, $w10-white);
  }

  .daterangepicker .calendar-table table {
    width: 100%;
    margin: 0;
    border-spacing: 0;
    border-collapse: collapse;
  }

  .daterangepicker td.available:hover, .daterangepicker th.available:hover {
    background-color: var(--warp-view-pagination-hover-bg-color, $w10-lightgrey);
    border-color: transparent;
    color: inherit;
  }

  .daterangepicker td.week, .daterangepicker th.week {
    font-size: 80%;
    color:  var(--warp-view-pagination-hover-color, $w10-lightgrey);
  }

  .daterangepicker td.off, .daterangepicker td.off.in-range, .daterangepicker td.off.start-date, .daterangepicker td.off.end-date {
    background-color: var(--warp-view-tooltip-bg-color, $w10-white);
    border-color: transparent;
    color: var(--warp-view-pagination-hover-color, $w10-charcoal);
  }

  .daterangepicker td.in-range {
    background-color: var(--warp-view-pagination-hover-bg-color, $w10-lightgrey);
    border-color: transparent;
    color:var(--warp-view-pagination-hover-color, $w10-charcoal);
    border-radius: 0;
  }

  .daterangepicker td.start-date {
    border-radius: 4px 0 0 4px;
  }

  .daterangepicker td.end-date {
    border-radius: 0 4px 4px 0;
  }

  .daterangepicker td.start-date.end-date {
    border-radius: 4px;
  }

  .daterangepicker td.active, .daterangepicker td.active:hover {
    background-color: var(--warp-view-button-bg-color, $w10-blue);
    border-color: transparent;
    color: var(--warp-view-button-label-color, $w10-white);
  }

  .daterangepicker th.month {
    width: auto;
  }

  .daterangepicker td.disabled, .daterangepicker option.disabled {
    color: var(--warp-view-button-secondary-label-color, $w10-charcoal);
    cursor: not-allowed;
    text-decoration: line-through;
  }

  .daterangepicker select.monthselect, .daterangepicker select.yearselect {
    font-size: 12px;
    padding: 1px;
    height: auto;
    margin: 0;
    cursor: default;
  }

  .daterangepicker select.monthselect {
    margin-right: 2%;
    width: 56%;
  }

  .daterangepicker select.yearselect {
    width: 40%;
  }

  .daterangepicker select.hourselect, .daterangepicker select.minuteselect, .daterangepicker select.secondselect, .daterangepicker select.ampmselect {
    width: 50px;
    margin: 0 auto;
    background: var(--warp-view-input-bg-color, $w10-white);
    border: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
    color: var(--warp-view-input-label-color, $w10-charcoal);
    padding: 2px;
    outline: 0;
    font-size: 12px;
  }

  .daterangepicker .calendar-time {
    text-align: center;
    margin: 4px auto 0 auto;
    line-height: 30px;
    position: relative;
  }

  .daterangepicker .calendar-time select.disabled {
    color: var(--warp-view-button-secondary-border-color, $w10-lightgrey);
    cursor: not-allowed;
  }

  .daterangepicker .drp-buttons {
    clear: both;
    text-align: right;
    padding: 8px;
    border-top: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
    display: none;
    line-height: 12px;
    vertical-align: middle;
  }

  .daterangepicker .drp-selected {
    display: inline-block;
    font-size: 12px;
    padding-right: 8px;
  }

  .daterangepicker .drp-buttons .btn {
    margin-left: 8px;
    font-size: 12px;
    font-weight: bold;
    padding: 4px 8px;
  }

  .daterangepicker.show-ranges.single.rtl .drp-calendar.left {
    border-right: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker.show-ranges.single.ltr .drp-calendar.left {
    border-left: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker.show-ranges.rtl .drp-calendar.right {
    border-right: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker.show-ranges.ltr .drp-calendar.left {
    border-left: 1px solid var(--warp-view-tooltip-border-color, $w10-lightgrey);
  }

  .daterangepicker .ranges {
    float: none;
    text-align: left;
    margin: 0;
    flex-grow: 1;
    max-height: 350px;
    max-width: 270px;
    overflow: auto;

    ul {
      width: 100%;
    }
  }

  .daterangepicker.show-calendar .ranges {
    margin-top: 8px;
  }

  .daterangepicker .ranges ul {
    list-style: none;
    margin: 0 auto;
    padding: 0;
    width: 100%;
  }

  .daterangepicker .ranges li {
    font-size: 12px;
    padding: 8px 12px;
    cursor: pointer;
  }

  .daterangepicker .ranges li:hover {
    background-color: var(--warp-view-pagination-hover-bg-color, $w10-lightgrey);
  }

  .daterangepicker .ranges li.active {
    background-color:var(--warp-view-button-bg-color, $w10-blue);
    color: var(--warp-view-button-label-color, $w10-white);
  }


  @media (min-width: 564px) {
    .daterangepicker {
      //      width: auto;
    }

    .daterangepicker .ranges ul {
      //    width: 140px;
    }

    .daterangepicker.single .ranges ul {
      width: 100%;
    }

    .daterangepicker.single .drp-calendar.left {
      clear: none;
    }

    .daterangepicker.single .ranges, .daterangepicker.single .drp-calendar {
      float: left;
    }

    .daterangepicker {
      direction: ltr;
      text-align: left;
    }

    .daterangepicker .drp-calendar.left {
      clear: left;
      margin-right: 0;
    }

    .daterangepicker .drp-calendar.left .calendar-table {
      border-right: none;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .daterangepicker .drp-calendar.right {
      margin-left: 0;
    }

    .daterangepicker .drp-calendar.right .calendar-table {
      border-left: none;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .daterangepicker .drp-calendar.left .calendar-table {
      padding-right: 8px;
    }

    .daterangepicker .ranges, .daterangepicker .drp-calendar {
      float: left;
    }
  }
  @media (min-width: 730px) {
    .daterangepicker .ranges {
      width: auto;
    }

    .daterangepicker .ranges {
      float: left;
    }

    .daterangepicker.rtl .ranges {
      float: right;
    }

    .daterangepicker .drp-calendar.left {
      clear: none !important;
    }
  }
}
